import React, { useEffect, useState } from 'react';
import { supabase } from '../App';
import EventTable from './EventTable';

const PAGE_SIZE = 1000;
const LONDON_CLOCK = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/London',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23'
});

const londonDate = (value = new Date()) => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit'
}).format(value);

const addDays = (dateString, count) => {
  const date = new Date(`${dateString}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + count);
  return date.toISOString().slice(0, 10);
};

const londonDayStart = (dateString) => {
  const guess = new Date(`${dateString}T00:00:00Z`);
  const parts = Object.fromEntries(LONDON_CLOCK.formatToParts(guess).map((part) => [part.type, part.value]));
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  return new Date(guess.getTime() - (asUtc - guess.getTime())).toISOString();
};

export default function EditLive() {
  const [events, setEvents] = useState([]);
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [filters, setFilters] = useState({ startDate: addDays(londonDate(), -1), endDate: '' });
  const [maxDate, setMaxDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('Finding available live events...');

  const loadLiveEvents = async (range = filters) => {
    if (!supabase) {
      setStatus('Supabase is not configured');
      setLoading(false);
      return;
    }
    if (!range.startDate || !range.endDate) return;
    setLoading(true);
    setStatus('Loading live events...');
    const loadedEvents = [];
    const rangeEnd = londonDayStart(addDays(range.endDate, 1));
    for (let from = 0; ; from += PAGE_SIZE) {
      const { data, error } = await supabase.from('live_events').select('*')
        .gte('start_local', londonDayStart(range.startDate))
        .lt('start_local', rangeEnd)
        .order('start_local', { ascending: true })
        .range(from, from + PAGE_SIZE - 1);
      if (error) {
        console.error('[Live events] load failed', error);
        setStatus(`Load failed: ${error.message}`);
        setLoading(false);
        return;
      }
      loadedEvents.push(...(data || []));
      if (!data || data.length < PAGE_SIZE) break;
    }
    setEvents(loadedEvents);
    setSelectedEventIds([]);
    setStatus(`${loadedEvents.length} live events`);
    setLoading(false);
  };

  useEffect(() => {
    const loadInitialRange = async () => {
      if (!supabase) {
        setStatus('Supabase is not configured');
        setLoading(false);
        return;
      }
      const { data, error } = await supabase.from('live_events').select('start_local')
        .order('start_local', { ascending: false })
        .limit(1);
      if (error) {
        console.error('[Live events] latest date load failed', error);
        setStatus(`Load failed: ${error.message}`);
        setLoading(false);
        return;
      }
      const endDate = data?.[0]?.start_local ? londonDate(new Date(data[0].start_local)) : addDays(londonDate(), -1);
      const initialFilters = { startDate: addDays(londonDate(), -1), endDate };
      setMaxDate(endDate);
      setFilters(initialFilters);
      await loadLiveEvents(initialFilters);
    };
    loadInitialRange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFilter = (name, value) => setFilters((current) => ({ ...current, [name]: value }));

  const updateLiveEvent = async (updated) => {
    setEvents((current) => current.map((event) => event.id === updated.id ? updated : event));
    const { id, event_lat, event_lon, created_at, published_at, updated_at, ...changes } = updated;
    const { error } = await supabase.from('live_events').update(changes).eq('id', id);
    if (error) {
      console.error('[Live events] update failed', error);
      setStatus(`Update failed: ${error.message}`);
      await loadLiveEvents();
      return;
    }
    setStatus('Live event saved');
  };

  const deleteLiveEvent = async (id) => {
    if (!window.confirm('Delete this live event?')) return;
    const { error } = await supabase.from('live_events').delete().eq('id', id);
    if (error) {
      setStatus(`Delete failed: ${error.message}`);
      return;
    }
    setEvents((current) => current.filter((event) => event.id !== id));
    setStatus('Live event deleted');
  };

  const deleteSelectedLiveEvents = async () => {
    if (!selectedEventIds.length || !window.confirm(`Delete ${selectedEventIds.length} selected live event${selectedEventIds.length === 1 ? '' : 's'}?`)) return;
    const { error } = await supabase.from('live_events').delete().in('id', selectedEventIds);
    if (error) {
      setStatus(`Delete failed: ${error.message}`);
      return;
    }
    setEvents((current) => current.filter((event) => !selectedEventIds.includes(event.id)));
    setSelectedEventIds([]);
    setStatus(`Deleted ${selectedEventIds.length} live event${selectedEventIds.length === 1 ? '' : 's'}`);
  };

  const toggleLiveEvent = (id) => setSelectedEventIds((current) => current.includes(id)
    ? current.filter((item) => item !== id)
    : [...current, id]);

  return <section className="live-panel"><div className="live-header"><div><span className="eyebrow">PUBLISHED DATA</span><h2>Edit live</h2></div><div className="live-actions"><span className="dashboard-toast">{status}</span><label>Start date<input type="date" value={filters.startDate} max={filters.endDate || maxDate || undefined} onChange={(event) => setFilter('startDate', event.target.value)} /></label><label>End date<input type="date" value={filters.endDate} min={filters.startDate || undefined} max={maxDate || undefined} onChange={(event) => setFilter('endDate', event.target.value)} /></label><button className="button primary" disabled={loading || !filters.startDate || !filters.endDate} onClick={() => loadLiveEvents()}>{loading ? 'Loading...' : 'Load'}</button></div></div>{events.length ? <EventTable events={events} selectedEventIds={selectedEventIds} onToggleEvent={toggleLiveEvent} onDeleteSelected={deleteSelectedLiveEvents} selectionLabel="Select" onDeleteEvent={deleteLiveEvent} onEventUpdate={updateLiveEvent} /> : <div className="empty-state">{loading ? 'Loading live events...' : 'No live events in this date range.'}</div>}</section>;
}
